import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";

import Container from "react-bootstrap/Container";

import ResetListState from "../../../components/ResetListState";
import Tip from "../../../components/Tip";
import { RawConstants, FILTER, DEFAULT_RAW_LIST_PAGE_SIZE, DEFAULT_RAW_FAX_PROGRAM_CODE } from "lookup/RawConstants";
import { RAW_LIST_COLUMNS, getRawListHiddenColumns } from "lookup/RawListColumns";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import ListHeaderBackButton from "components/ListHeaderBackButton";
import { useSyncListPaginationToUrl } from "commons/hooks/useSyncListPaginationToUrl";
import { showNotification } from "services/NotificationService";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import {
  getInitialPagination,
  stripReferrerSort,
  sortedModification,
  filterModification,
  isResetDisabled,
  renderOwnerCell,
  renderStatusCell,
  renderEffectiveDateCol,
  renderLastModifiedDateCol,
  renderSavingsCell,
  getUrlFilterValues,
  buildInitialPaginationFromUrl,
  buildSearchFromPagination,
} from "./RawList.helpers";

const RAW_PROGRAM_TYPE_LIST_PATH = "/raw/program-type-list";

const RawList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagination, setPagination] = useState(() =>
    buildInitialPaginationFromUrl(location.search, {
      shouldReset: location.state?.reset,
    })
  );
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [totalElements, setTotalElements] = useState("Loading");

  const urlFilterValues = getUrlFilterValues(location.search);
  const faxProgramCode =
    urlFilterValues.faxProgramCode || DEFAULT_RAW_FAX_PROGRAM_CODE;

  const requestIdRef = useRef(0);

  const fetchRawList = useCallback((state) => {
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setTotalElements("Loading");
    setPagination((prev) => ({
      ...prev,
      pageSize: state.pageSize,
      page: state.page,
      sorted: state.sorted ?? prev.sorted,
      filtered: state.filtered ?? prev.filtered,
    }));

    const filteredWithProgramType = [
      { id: "faxProgramType", value: faxProgramCode, isdefault: true },
      ...filterModification(state.filtered ?? []).filter(
        (f) => f.id !== "faxProgramType"
      ),
    ];

    sendAuthenticatedAsyncRequest(
      "/raw/list",
      "POST",
      {
        pageSize: state.pageSize,
        page: state.page,
        sorted: sortedModification(state.sorted ?? []),
        filtered: filteredWithProgramType,
      },
      (res) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (res.status === 200) {
          const payload = res.data;
          setData(payload.records ?? []);
          setTotalPages(payload.totalPages ?? 0);
          setTotalElements(payload.totalElements ?? 0);
          setPagination((prev) => ({
            ...prev,
            page: payload.page ?? state.page,
          }));
          setIsError(false);
          setLoading(false);
          return;
        }
        setData([]);
        setTotalPages(0);
        setLoading(false);
        setIsError(true);
        setTotalElements("Failed");
      },
      () => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setData([]);
        setTotalPages(0);
        setLoading(false);
        setIsError(true);
        setTotalElements("Failed");
      }
    );
  }, [faxProgramCode]);

  const updateData = useCallback(
    (state, preventUpdate = false) => {
      if (!preventUpdate) {
        fetchRawList(state);
      }
    },
    [fetchRawList]
  );

  useEffect(() => {
    fetchRawList(pagination);
  }, [fetchRawList]);

  useSyncListPaginationToUrl({
    pagination,
    buildSearchFromPagination,
    navigate,
    location,
  });

  useEffect(() => {
    if (!isError) {
      return;
    }
    showNotification({
      title: "Error Loading Data!",
      message: "Something went Wrong.",
      position: "bl",
      type: "error",
    });
    setIsError(false);
  }, [isError]);

  const onUpdateTable = (filterValues, sortBy, pageSize, pageNo) => {
    const newState = {
      ...pagination,
      pageSize,
      page: pageNo,
      sorted: sortBy ?? [],
      filtered: filterValues ?? [],
    };
    setPagination(newState);
    updateData(newState);
  };

  const handleReset = () => {
    const resetState = {
      ...pagination,
      filtered: [FILTER.defaultBackend],
      sorted: RawConstants.DEFAULT_SORT,
    };
    setPagination(resetState);
    updateData(resetState);
  };

  const tableSorted = stripReferrerSort(pagination.sorted);

  const listColumns = useMemo(
    () =>
      RAW_LIST_COLUMNS(
        {
          renderStatusCell,
          renderOwnerCell,
          renderLastModifiedDateCol,
          renderEffectiveDateCol,
          renderSavingsCell,
          statusFilterMap: RawConstants.statusesMultiSelect,
        },
        getRawListHiddenColumns(urlFilterValues.faxProgramCode)
      ),
    [urlFilterValues.faxProgramCode]
  );

  return (
    <Container className="list-container" fluid>
      <div className="fax-program-list-panel">
        <div className="codex-version-display-bar fax-program-list-panel__header">
          <ListHeaderBackButton
            onClick={() => navigate(RAW_PROGRAM_TYPE_LIST_PATH)}
          />
          <span className="list-header-meta">
            Total Responses: <strong>{totalElements}</strong>
          </span>
          <strong className="list-header-title">
            {urlFilterValues.faxProgramCode
              ? `${urlFilterValues.faxProgramCode} Responses`
              : "Response List"}
          </strong>
          <div className="list-header-actions">
            <ResetListState
              onReset={handleReset}
              resetterDisabled={isResetDisabled(pagination.filtered, pagination.sorted)}
            />
          </div>
        </div>
        <div className="fax-program-list-panel__body">
          <ServerSideReactTable
            columnDefination={listColumns}
            data={data}
            currentPage={pagination.page}
            sorted={tableSorted}
            filters={pagination.filtered}
            totalPages={totalPages}
            totalRecords={totalElements === "Loading" ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            defaultPageSize={DEFAULT_RAW_LIST_PAGE_SIZE}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            onUpdateTableData={onUpdateTable}
            onRowClick={(rowInfo) => {
              if (rowInfo !== undefined) {
                navigate(`/raw/${rowInfo.original.rawId}${location.search || ""}`);
              }
            }}
          />
          <Tip>Tip: Hold shift when sorting to multi-sort and disable sort!</Tip>
        </div>
      </div>
    </Container>
  );
};

const RawListWithDefaultQuery = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search || "");
  const faxProgramCode = params.get("faxProgramCode");

  if (faxProgramCode != null && faxProgramCode !== "") {
    return <RawList />;
  }

  params.set("faxProgramCode", DEFAULT_RAW_FAX_PROGRAM_CODE);
  return (
    <Navigate
      to={`${location.pathname}?${params.toString()}`}
      replace
      state={location.state}
    />
  );
};

export default RawListWithDefaultQuery;
